import Layout from 'components/layout';
import { GTM_VIEW_EXP_JOIN_PAGE } from 'constants/events';
import { useEffect, useRef } from 'react';
import { trackEvent } from 'utils/google-tag-manager';
import FormPage from 'components/form-page';
import Link from 'next/link';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { TenantTypes, isTenantExpCA } from '@tenants';
import { NOT_FOUND } from 'constants/not-found';
import { FeaturesType } from 'contexts';
import { useTranslation } from 'next-i18next';
import { LOCALE_JOIN_EXP_CA_PATHS, Namespaces } from 'types/locales';
import { JoinPageObservabilityService } from 'observability/services/join-page-observability';
import { v4 as uuid } from 'uuid';

import type { GetServerSidePropsContext } from 'next';
import HubspotForm from 'components/hubspot-form';

const HUBSPOT_FORM_PORTAL_ID = '24210079';
const HUBSPOT_FORM_ID = '70b1bd37-99bb-4478-b248-0965b3501ec1';

const handler: HandlerType = async (_context: GetServerSidePropsContext, features?: FeaturesType, tenant?: TenantTypes) => {
  if (!isTenantExpCA(tenant.id, features)) {
    return NOT_FOUND;
  }
  return { props: {}};
};

export const getServerSideProps = createGetServerSideProps(handler);

export default function JoinExpCAContact() {
  const { t, i18n } = useTranslation(Namespaces.JOIN_EXP_CA);
  const formSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    trackEvent(GTM_VIEW_EXP_JOIN_PAGE);
  }, []);

  const handleFormReady = () => {
    formSessionIdRef.current = uuid();
  };

  const handleFormSubmitError = (error: { message?: string }) => {
    if (!formSessionIdRef.current) return;

    try {
      JoinPageObservabilityService.trackFormSubmitFailure({
        error: error.message || 'Form validation failed',
        sessionId: formSessionIdRef.current,
      });
    } catch (err) {
      console.error('[Join Form] Failed to track submit failure:', err);
    }
  };

  const NotificationContent = () => (
    <p>
      {t(LOCALE_JOIN_EXP_CA_PATHS.CONTACT_PAGE_NOTIFICATION_TEXT, {
        defaultValue: 'Interested in joining the commercial waitlist?',
      })}{' '}
      <Link href="/commercial-waitlist-ca">
        {t(LOCALE_JOIN_EXP_CA_PATHS.CONTACT_PAGE_NOTIFICATION_LINK_TEXT, {
          defaultValue: 'Sign Up Here',
        })}
      </Link>
    </p>
  );

  return (
    <Layout>
      <FormPage
        title={t(LOCALE_JOIN_EXP_CA_PATHS.CONTACT_PAGE_TITLE, {
          defaultValue: 'Contact eXp Realty Canada',
        })}
        description={t(LOCALE_JOIN_EXP_CA_PATHS.CONTACT_PAGE_DESCRIPTION, {
          defaultValue: "We'd love to hear from you! Fill out the form below and our team will get back to you as soon as possible.",
        })}
        notificationContent={<NotificationContent />}
        formContent={
          <HubspotForm 
            portalId={HUBSPOT_FORM_PORTAL_ID} 
            formId={HUBSPOT_FORM_ID} 
            region="na1" 
            locale={i18n.language}
            onFormReady={handleFormReady}
            onFormSubmitError={handleFormSubmitError}
          />
        }
      />
    </Layout>
  );
}